import { Injectable } from '@angular/core';
import { CanMatch, Route, UrlSegment } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, timeout } from 'rxjs/operators';

import { LaunchDarklyService } from '@services/launchdarkly.service';
import { UserService } from '@services/user.service';

export const DATA_CATEGORIZATION_REDESIGN_FLAG = 'data-categorization-redesign';

// How long to wait for LaunchDarkly to settle the identified user's flags
// before falling back to the dev path.
const FLAGS_READY_TIMEOUT_MS = 4000;

/**
 * Route `canMatch` guard that selects the redesigned (v2) Data Categorization
 * page when the `data-categorization-redesign` flag is on, and otherwise lets
 * routing fall through to the legacy (dev) page.
 *
 * The flag is targeted per tenant, so it is only correct after the LaunchDarkly
 * client has identified the user. Identification is triggered by the route's
 * `AuthMiddleware.canActivate`, which runs AFTER `canMatch` — so this guard
 * triggers it itself via `getCurrentUser()`, then waits for `flagsReady$` (the
 * identified-context signal, with a bounded timeout) before reading the flag
 * once. Without this, a hard refresh would read the anonymous bootstrap flags
 * (target rule not applied) and incorrectly fall through to the legacy page.
 *
 * The guard is not re-evaluated for a mounted component, so the chosen version
 * is fixed for the lifetime of the page and only changes on the next navigation.
 */
@Injectable({ providedIn: 'root' })
export class DataCategorizationRedesignMatch implements CanMatch {
  constructor(
    private _launchDarklyService: LaunchDarklyService,
    private _userService: UserService,
  ) {}

  canMatch(_route: Route, _segments: UrlSegment[]): Observable<boolean> {
    // Ensure the user is identified before reading the targeted flag. On a hard
    // refresh nothing has identified the user yet (canActivate runs later), so
    // we trigger it here; `getCurrentUser()` is cached, so AuthMiddleware's
    // later call reuses the same user without a second request.
    return this._userService.getCurrentUser().pipe(
      switchMap(() => this._launchDarklyService.flagsReady$),
      filter(ready => ready),
      take(1),
      map(() => !!this._launchDarklyService.flags[DATA_CATEGORIZATION_REDESIGN_FLAG]),
      // Fallback must come AFTER the map, so the timeout's `false` is the final
      // value. (With map last, the fallback's `false` would be overwritten by
      // re-reading the flag — which is `true` when the flag is on.)
      timeout({ first: FLAGS_READY_TIMEOUT_MS, with: () => of(false) }),
      // Identification failure must not break routing — fall back to legacy.
      catchError(() => of(false)),
    );
  }
}
